<template>
    <div v-show="showModal" class="pwa-install-modal">
        <div class="pwa-install-modal-mask"></div>
        <div class="pwa-install-modal-dialog">
            <div class="pwa-install-modal-header">
                <div class="pwa-install-modal-header-title">
                    <img loading="lazy" src="/assets/pwa/ic_raid_install.png" alt="" />
                    <span>Quick Install</span>
                </div>
            </div>
            <div class="pwa-install-modal-main">
                <!-- Installing 状态 -->
                <div v-if="installState === 'INSTALLING'" class="pwa-install-modal-loading">
                    <div class="loading-circle">
                        <svg viewBox="25 25 50 50">
                            <circle cx="50" cy="50" fill="none" r="20"></circle>
                        </svg>
                    </div>
                    <span class="loading-text">{{ installProgress }}%</span>
                </div>
                
                <!-- Play 状态 -->
                <div v-else-if="installState === 'PLAY'" class="pwa-install-modal-play">
                    <button @click="handlePlayClick" class="play-button">
                        <span>Play</span>
                    </button>
                    <div class="play-text">
                        <span>Tap to play now</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import useApp from 'core/hooks/useApp';

const { mitt } = useApp();

const emit = defineEmits(['play']);

// 弹窗状态管理
const showModal = ref(false);
const installProgress = ref(0);
const installState = ref<'INSTALLING' | 'PLAY'>('INSTALLING');

let installTimer: ReturnType<typeof setInterval> | null = null;

// 处理Play按钮点击
const handlePlayClick = () => {
    // 关闭弹窗
    showModal.value = false;
    installState.value = 'INSTALLING';
    installProgress.value = 0;
    
    // 触发play事件
    emit('play');
};

// 监听PWA安装开始事件
onMounted(() => {
    mitt.on('PWA_START_INSTALL', (eventData: any) => {
        console.log('PWA安装状态组件：监听到PWA_START_INSTALL', eventData);
        
        // 如果是web-temp-v3组件发起的安装，不显示全局modal（它有自己的loading）
        if (eventData?.source === 'web-temp-v3') {
            console.log('web-temp-v3有自己的loading，跳过全局modal');
            return;
        }
        
        console.log('显示全局PWA安装loading弹窗');
        // 显示loading弹窗
        showModal.value = true;
        installProgress.value = 0;
        installState.value = 'INSTALLING';
        
        // 开始10秒安装进度
        startInstallProgress();
    });
});

// 开始安装进度
const startInstallProgress = () => {
    const duration = 10000; // 10秒
    const interval = 100; // 更新间隔
    const steps = duration / interval;
    const increment = 100 / steps;

    installTimer = setInterval(() => {
        if (installProgress.value >= 100) {
            clearInterval(installTimer!);
            installTimer = null;
            
            // 安装完成，切换到Play状态
            installState.value = 'PLAY';
            installProgress.value = 100;
            return;
        }
        installProgress.value = Math.min(100, installProgress.value + increment);
    }, interval);
};

// 组件卸载时清理定时器和事件监听
onUnmounted(() => {
    if (installTimer) {
        clearInterval(installTimer);
    }
    mitt.off('PWA_START_INSTALL');
});
</script>

<style scoped lang="scss">
// PWA Install Modal Styles
.pwa-install-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 3000;
}

.pwa-install-modal-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    animation: fadeIn 0.3s ease-in-out;
}

.pwa-install-modal-dialog {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #fff;
    border-radius: 10px;
    padding: 32px 24px 24px;
    width: 280px;
    max-width: 90vw;
    animation: slideIn 0.3s ease-in-out;
}

.pwa-install-modal-header {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
}

.pwa-install-modal-header-title {
    display: flex;
    align-items: center;
    
    img {
        width: 28px;
        margin-right: 7px;
    }
    
    span {
        color: #666;
        font-size: 20px;
        font-weight: 400;
        letter-spacing: 1px;
        line-height: 33px;
    }
}

.pwa-install-modal-main {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 120px;
}

// Loading State
.pwa-install-modal-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #028760;
    
    .loading-circle {
        width: 60px;
        height: 60px;
        margin-bottom: 16px;
        animation: loading-rotate 2s linear infinite;
        
        svg {
            width: 100%;
            height: 100%;
            color: #028760;
            
            circle {
                stroke: currentColor;
                stroke-width: 3;
                stroke-linecap: round;
                fill: none;
                animation: loading-circular 1.5s ease-in-out infinite;
            }
        }
    }
    
    .loading-text {
        font-size: 18px;
        font-weight: 600;
        color: #028760;
    }
}

// Play State Styles
.pwa-install-modal-play {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    text-align: center;
}

.play-button {
    background-color: #028760;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    margin-bottom: 15px;

    &:hover {
        background-color: #016a4a;
    }

    &:active {
        transform: scale(0.95);
    }
}

.play-text {
    color: #666;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.5px;
    line-height: 20px;
}

// Animations
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideIn {
    from { 
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.9);
    }
    to { 
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
    }
}

@keyframes loading-rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@keyframes loading-circular {
    0% {
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -40;
    }
    100% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -120;
    }
}
</style> 